package example.shadcn_kotlin.ui.pages.docs.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*
import example.shadcn_kotlin.ui.components.*
import example.shadcn_kotlin.ui.pages.components.demo.DialogDemo

@Page
@Composable
fun DialogPage() {
    CompositionLocalProvider(LocalMarkdownContext provides MarkdownContext("docs/components/dialog.md", mapOf("title" to listOf("Dialog"), "description" to listOf("A window overlaid on either the primary window or another dialog window, rendering the content underneath inert."), "radix" to listOf(), "link" to listOf("https://www.radix-ui.com/docs/primitives/components/dialog"), "api" to listOf("https://www.radix-ui.com/docs/primitives/components/dialog#api-reference")))) {
        example.shadcn_kotlin.ui.components.layouts.MDLayout {
            ComponentPreview("""import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import shadcn.ui.components.*
import web.cssom.ClassName
import web.html.ButtonType

val DialogDemo = FC<Props> {
    Dialog {
        DialogTrigger {
            asChild = true
            Button {
                variant = "outline"
                +"Edit Profile"
            }
        }
        DialogContent {
            className = ClassName("sm:max-w-[425px]")
            DialogHeader {
                DialogTitle {
                    +"Edit profile"
                }
                DialogDescription {
                    +"Make changes to your profile here . Click save when you 're done."
                }
            }
            div {
                className = ClassName("grid gap-4 py-4")
                div {
                    className = ClassName("grid grid-cols-4 items-center gap-4")
                    Label {
                        htmlFor = "name"
                        className = ClassName("text-right")
                        +"Name"
                    }
                    Input {
                        id = "name"
                        defaultValue = "Pedro Duarte"
                        className = ClassName("col-span-3")
                    }
                }
                div {
                    className = ClassName("grid grid-cols-4 items-center gap-4")
                    Label {
                        htmlFor = "username"
                        className = ClassName("text-right")
                        +"Username"
                    }
                    Input {
                        id = "username"
                        defaultValue = "@peduarte"
                        className = ClassName("col-span-3")
                    }
                }
            }
            DialogFooter {
                Button {
                    type = ButtonType.submit
                    +"Save changes"
                }
            }
        }
    }
}"""){ 
 DialogDemo {} 
 }
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("usage")
 }) {
                org.jetbrains.compose.web.dom.Text("Usage")
            }
            CodeBlock("""import shadcn.ui.components.Dialog
import shadcn.ui.components.DialogContent
import shadcn.ui.components.DialogDescription
import shadcn.ui.components.DialogHeader
import shadcn.ui.components.DialogTitle
import shadcn.ui.components.DialogTrigger
""", lang= "kotlin", highlight= "")
            CodeBlock("""Dialog {
    DialogTrigger { + "Open" }
    DialogContent {
        DialogHeader {
            DialogTitle { + "Are you sure absolutely sure?" }
            DialogDescription {
                + "This action cannot be undone. This will permanently delete your account "
                + "and remove your data from our servers."
            }
        }
    }
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("notes")
 }) {
                org.jetbrains.compose.web.dom.Text("Notes")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("To activate the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Dialog")
                }
                org.jetbrains.compose.web.dom.Text(" component from within a ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Context Menu")
                }
                org.jetbrains.compose.web.dom.Text(" or ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Dropdown Menu")
                }
                org.jetbrains.compose.web.dom.Text(", you must encase the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Context Menu")
                }
                org.jetbrains.compose.web.dom.Text(" or ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Dropdown Menu")
                }
                org.jetbrains.compose.web.dom.Text(" component in the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("Dialog")
                }
                org.jetbrains.compose.web.dom.Text(" component. For more information, refer to the linked issue ")
                org.jetbrains.compose.web.dom.A("https://github.com/radix-ui/primitives/issues/1836", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("here")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
            CodeBlock("""Dialog {
    ContextMenu {
        ContextMenuTrigger { +"Right click" }
        ContextMenuContent {
            ContextMenuItem { +"Open" }
            ContextMenuItem { +"Download" }
            DialogTrigger {
                asChild = true
                ContextMenuItem {
                    span { +"Delete" }
                }
            }
        }
    }
    DialogContent {
        DialogHeader {
            DialogTitle { +"Are you sure absolutely sure?" }
            DialogDescription {
                +"This action cannot be undone. This will permanently delete your account "
                +"and remove your data from our servers."
            }
        }
        DialogFooter {
            Button {
                type = ButtonType.submit
                +"Confirm"
            }
        }
    }
}
""", lang= "kotlin", highlight= "14-25")
        }
    }
}
